import React from 'react';
import RoleGuard from '../RoleGuard';
import './Toolbar.scss';

// ── Button config ─────────────────────────────────────────────────────────────

export interface ToolbarButtonConfig {
  /** Unique key for React reconciliation. */
  key: string;
  /** Icon node (e.g. <FaPlus />). */
  icon?: React.ReactNode;
  /** Optional visible label rendered beside the icon. */
  label?: string;
  /** Callback fired when the button is clicked. */
  onClick: () => void;
  /** Tooltip text; falls back to label when omitted. */
  title?: string;
  /** Visual colour variant. */
  variant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'info';
  /** Role string forwarded to RoleGuard. Leave empty to show for all roles. */
  role?: string;
  disabled?: boolean;
}

// ── Filter configs ────────────────────────────────────────────────────────────

export interface ToolbarSelectFilter {
  key: string;
  type: 'select';
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
  /** When provided a leading "show all" option with an empty value is prepended. */
  allLabel?: string;
  width?: string | number;
}

export interface ToolbarCheckboxFilter {
  key: string;
  type: 'checkbox';
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

export interface ToolbarTextFilter {
  key: string;
  type: 'text';
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: string | number;
}

export interface ToolbarCustomFilter {
  key: string;
  type: 'custom';
  /** Optional label rendered above the custom control (same style as select/text labels). */
  label?: string;
  /** Render callback for the custom control (e.g. a searchable SelectInput). */
  render: () => React.ReactNode;
}

export type ToolbarFilterConfig =
  | ToolbarSelectFilter
  | ToolbarCheckboxFilter
  | ToolbarTextFilter
  | ToolbarCustomFilter;

// ── Props ─────────────────────────────────────────────────────────────────────

export interface ToolbarProps {
  /** Action buttons rendered on the right side of the toolbar. */
  buttons?: ToolbarButtonConfig[];
  /** Filter controls rendered on the left side of the toolbar. */
  filters?: ToolbarFilterConfig[];
  className?: string;
}

// ── Filter renderer ───────────────────────────────────────────────────────────

function renderFilter(filter: ToolbarFilterConfig): React.ReactNode {
  switch (filter.type) {
    case 'select':
      return (
        <div key={filter.key} className="toolbar__filter">
          {filter.label && (
            <span className="toolbar__filter-label">{filter.label}</span>
          )}
          <select
            className="toolbar__filter-select"
            value={filter.value}
            onChange={e => filter.onChange(e.target.value)}
            style={filter.width ? { width: filter.width } : undefined}
          >
            {filter.allLabel !== undefined && (
              <option value="">{filter.allLabel}</option>
            )}
            {filter.options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      );

    case 'checkbox':
      return (
        <div key={filter.key} className="toolbar__filter toolbar__filter--checkbox">
          <label className="toolbar__filter-checkbox-label" htmlFor={`tbf-${filter.key}`}>
            <input
              id={`tbf-${filter.key}`}
              type="checkbox"
              className="toolbar__filter-checkbox-input"
              checked={filter.value}
              onChange={e => filter.onChange(e.target.checked)}
            />
            <span>{filter.label}</span>
          </label>
        </div>
      );

    case 'text':
      return (
        <div key={filter.key} className="toolbar__filter">
          {filter.label && (
            <span className="toolbar__filter-label">{filter.label}</span>
          )}
          <input
            type="text"
            className="toolbar__filter-text"
            value={filter.value}
            onChange={e => filter.onChange(e.target.value)}
            placeholder={filter.placeholder}
            style={filter.width ? { width: filter.width } : undefined}
          />
        </div>
      );

    case 'custom':
      return (
        <div key={filter.key} className="toolbar__filter toolbar__filter--custom">
          {filter.label && (
            <span className="toolbar__filter-label">{filter.label}</span>
          )}
          {filter.render()}
        </div>
      );
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

const Toolbar: React.FC<ToolbarProps> = ({ buttons = [], filters = [], className }) => {
  const hasFilters = filters.length > 0;
  const hasButtons = buttons.length > 0;
  if (!hasFilters && !hasButtons) return null;

  return (
    <div className={`toolbar${className ? ` ${className}` : ''}`}>
      {hasFilters && (
        <div className="toolbar__filters">
          {filters.map(f => renderFilter(f))}
        </div>
      )}
      {hasButtons && (
        <div className="toolbar__actions">
          {buttons.map(btn => (
            <RoleGuard key={btn.key} roles={btn.role ?? ''}>
              <button
                className={`toolbar__btn toolbar__btn--${btn.variant ?? 'primary'}`}
                onClick={btn.onClick}
                title={btn.title ?? btn.label}
                disabled={btn.disabled}
              >
                {btn.icon  && <span className="toolbar__btn-icon">{btn.icon}</span>}
                {btn.label && <span className="toolbar__btn-label">{btn.label}</span>}
              </button>
            </RoleGuard>
          ))}
        </div>
      )}
    </div>
  );
};

export default Toolbar;

